/**
 * Test Definitions - Language Selector
 *
 * This module provides access to localized test definitions.
 * Tests are organized by language in subdirectories (es/, en/).
 *
 * Usage:
 *   import { getTestDefinitions, getTestById } from './tests';
 *   const tests = getTestDefinitions('en'); // Get all tests in English
 *   const gad7 = getTestById('gad-7', 'es'); // Get specific test in Spanish
 */

import { TestDefinition } from '../types';
import { Language } from '../i18n/config';

// Import test definitions by language
import { TEST_DEFINITIONS as TESTS_ES } from './es';
import { TEST_DEFINITIONS as TESTS_EN } from './en';

/**
 * Map of language codes to test definitions
 * Note: English tests fallback to Spanish via testLoader.ts if specific categories
 * haven't been translated yet. Here we only include fully translated tests.
 */
const TEST_MAPS: Record<Language, TestDefinition[]> = {
  es: TESTS_ES,
  // Use Spanish as fallback while English tests are being translated
  // Once TEST_EN has content, it will be used directly
  en: TESTS_EN.length > 0 ? TESTS_EN : TESTS_ES,
};

/**
 * Get all test definitions for a language
 * @param lang - Language code ('es' | 'en')
 * @returns Array of test definitions in the specified language
 */
export function getTestDefinitions(lang: Language = 'es'): TestDefinition[] {
  return TEST_MAPS[lang] || TEST_MAPS.es;
}

/**
 * Get a specific test by ID
 * @param id - Test ID (e.g., 'gad-7', 'phq-9')
 * @param lang - Language code
 * @returns Test definition or undefined if not found
 */
export function getTestById(id: string, lang: Language = 'es'): TestDefinition | undefined {
  return getTestDefinitions(lang).find(t => t.id === id);
}

/**
 * Get a specific test by code
 * @param code - Test code (e.g., 'GAD-7', 'PHQ-9')
 * @param lang - Language code
 * @returns Test definition or undefined if not found
 */
export function getTestByCode(code: string, lang: Language = 'es'): TestDefinition | undefined {
  return getTestDefinitions(lang).find(t => t.code === code);
}

/**
 * Get available test categories for a language
 * @param lang - Language code
 * @returns Array of unique category names
 */
export function getTestCategories(lang: Language = 'es'): string[] {
  const tests = getTestDefinitions(lang);
  const categories = new Set(tests.map(t => t.category));
  return Array.from(categories).sort();
}

/**
 * Get tests filtered by category
 * @param category - Category name
 * @param lang - Language code
 * @returns Array of test definitions in the category
 */
export function getTestsByCategory(category: string, lang: Language = 'es'): TestDefinition[] {
  return getTestDefinitions(lang).filter(t => t.category === category);
}

// =============================================================================
// BACKWARDS COMPATIBILITY
// These exports maintain compatibility with existing code that imports directly
// =============================================================================

/**
 * @deprecated Use getTestDefinitions('es') instead
 */
export const TEST_DEFINITIONS = TESTS_ES;

/**
 * Tests available for FREE tier (20 most common in clinical practice)
 */
export const FREE_TIER_TEST_CODES = [
  // Depression (4)
  'PHQ-9',
  'BDI-II',
  'HAM-D',
  'MADRS',
  // Anxiety (5)
  'GAD-7',
  'BAI',
  'HAM-A',
  'STAI',
  'HADS',
  // Trauma (2)
  'PCL-5',
  'IES-R',
  // Addictions (2)
  'AUDIT',
  'DAST-10',
  // Suicide Risk (2)
  'C-SSRS',
  'BSSI',
  // Bipolar (2)
  'YMRS',
  'MDQ',
  // OCD (1)
  'Y-BOCS',
  // Stress (1)
  'PSS-10',
  // Functionality (1)
  'GAF',
];

// Re-export from Spanish for backwards compatibility
export {
  TESTS_DEPRESION,
  TESTS_ANSIEDAD,
  TESTS_BIPOLARIDAD,
  TESTS_TRAUMA,
  TESTS_ADICCIONES,
  TESTS_RIESGO_SUICIDA,
  TESTS_PSICOSIS,
  TESTS_FUNCIONALIDAD,
  TESTS_PEDIATRICO_TDAH,
  TESTS_TDAH_ADULTOS,
  TESTS_PERSONALIDAD,
  TESTS_16_PERSONALITY,
  TESTS_DISCAPACIDAD,
  TESTS_TOC,
  TESTS_ALIMENTARIOS,
  TESTS_ESTRES,
  TESTS_CALIDAD_VIDA
} from './es';
